// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import clsx from 'clsx';

import { useAppLayoutToolbarDesignEnabled } from '../app-layout/utils/feature-flags';
import { getBaseProps } from '../internal/base-component';
import { fireCancelableEvent, fireNonCancelableEvent } from '../internal/events';
import { InternalBaseComponentProps } from '../internal/hooks/use-base-component';
import { isDevelopment } from '../internal/is-development';
import { createWidgetizedComponent } from '../internal/widgets';
import { SideNavigationProps } from './interfaces';
import { Header, NavigationItemsList } from './parts';
import {
  checkCollapsedIconSupport,
  checkDuplicateHrefs,
  generateExpandableItemsMapping,
  hasNavigationIcons,
} from './util';

import styles from './styles.css.js';

type SideNavigationInternalProps = SideNavigationProps & InternalBaseComponentProps;

export function SideNavigationImplementation({
  header,
  itemsControl,
  activeHref,
  items = [],
  onFollow,
  onChange,
  collapsed = false,
  __internalRootRef,
  ...props
}: SideNavigationInternalProps) {
  const baseProps = getBaseProps(props);
  const isToolbar = useAppLayoutToolbarDesignEnabled();
  const parentMap = useMemo(() => generateExpandableItemsMapping(items), [items]);
  const withIcons = useMemo(() => hasNavigationIcons(items), [items]);
  const [activeTooltip, setActiveTooltip] = useState<string | null>(null);

  const listContainerRef = useRef<HTMLDivElement>(null);

  if (isDevelopment) {
    // This code should be wiped in production anyway.
    // eslint-disable-next-line react-hooks/rules-of-hooks
    useEffect(() => checkDuplicateHrefs(items), [items]);
    // eslint-disable-next-line react-hooks/rules-of-hooks
    useEffect(() => checkCollapsedIconSupport(items, collapsed), [items, collapsed]);
  }

  const onChangeHandler = useCallback(
    (item: SideNavigationProps.Section | SideNavigationProps.ExpandableLinkGroup, expanded: boolean) => {
      // generateExpandableItemsMapping walks through the entire tree, so we're certain about getting a value.
      fireNonCancelableEvent(onChange, { item, expanded: expanded, expandableParents: parentMap.get(item)! });
    },
    [onChange, parentMap]
  );

  const onFollowHandler = useCallback(
    (
      item:
        | SideNavigationProps.Link
        | SideNavigationProps.Header
        | SideNavigationProps.LinkGroup
        | SideNavigationProps.ExpandableLinkGroup,
      sourceEvent: React.SyntheticEvent | Event
    ) => {
      fireCancelableEvent(onFollow, item, sourceEvent);
    },
    [onFollow]
  );

  return (
    <div
      {...baseProps}
      className={clsx(
        styles.root,
        baseProps.className,
        isToolbar && styles['with-toolbar'],
        collapsed && styles['root--collapsed']
      )}
      ref={__internalRootRef}
    >
      {header && (
        <Header
          definition={header}
          activeHref={activeHref}
          fireChange={onChangeHandler}
          fireFollow={onFollowHandler}
          collapsed={collapsed}
        />
      )}
      {!collapsed && itemsControl && <div className={styles['items-control']}>{itemsControl}</div>}
      {items && (
        <div ref={listContainerRef} className={styles['list-container']}>
          <NavigationItemsList
            variant="root"
            items={items}
            fireFollow={onFollowHandler}
            fireChange={onChangeHandler}
            activeHref={activeHref}
            collapsed={collapsed}
            withIcons={withIcons}
            activeTooltip={activeTooltip}
            setActiveTooltip={setActiveTooltip}
          />
        </div>
      )}
    </div>
  );
}

export const createWidgetizedSideNavigation = createWidgetizedComponent(SideNavigationImplementation);



// WEBPACK FOOTER //
// /local/p4clients/pkgbuild-const/src/side-navigation/implementation.tsx